'use client';

import React from 'react';
import Link from 'next/link';
import { Smartphone, Bike, Briefcase, Laptop, Shirt, Waves, Camera, Key, MoreHorizontal, AlertTriangle, ShieldCheck, ChevronRight } from 'lucide-react';

const items = [
  {
    id: 'item-001',
    name: 'iPhone 15 Pro',
    category: 'Phone',
    brand: 'Apple',
    status: 'PROTECTED',
    labelId: 'CARD-A1',
    lastScan: null,
    Icon: Smartphone,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
  },
  {
    id: 'item-002',
    name: 'Honda CB500F',
    category: 'Motorbike',
    brand: 'Honda',
    status: 'PROTECTED',
    labelId: 'RT-M44X',
    lastScan: '3 days ago',
    Icon: Bike,
    iconBg: 'bg-orange-50',
    iconColor: 'text-orange-600',
  },
  {
    id: 'item-003',
    name: 'Black Samsonite Suitcase',
    category: 'Luggage',
    brand: 'Samsonite',
    status: 'LOST',
    labelId: 'RT-P19L',
    lastScan: 'Today 15:08',
    Icon: Briefcase,
    iconBg: 'bg-red-50',
    iconColor: 'text-red-500',
  },
  {
    id: 'item-004',
    name: 'MacBook Air M3',
    category: 'Laptop',
    brand: 'Apple',
    status: 'PROTECTED',
    labelId: 'RT-L72Q',
    lastScan: null,
    Icon: Laptop,
    iconBg: 'bg-slate-50',
    iconColor: 'text-slate-600',
  },
  {
    id: 'item-005',
    name: 'North Face Gore-Tex',
    category: 'Clothing',
    brand: 'The North Face',
    status: 'PROTECTED',
    labelId: 'RT-K72M',
    lastScan: 'Today 18:42',
    Icon: Shirt,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
  },
  {
    id: 'item-006',
    name: 'Surf Wetsuit',
    category: 'Sports Gear',
    brand: "O\'Neill",
    status: 'PROTECTED',
    labelId: 'RT-W33P',
    lastScan: null,
    Icon: Waves,
    iconBg: 'bg-cyan-50',
    iconColor: 'text-cyan-600',
  },
  {
    id: 'item-007',
    name: 'Camera Bag',
    category: 'Bag',
    brand: 'Lowepro',
    status: 'PROTECTED',
    labelId: 'RT-C91Z',
    lastScan: '1 week ago',
    Icon: Camera,
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-600',
  },
  {
    id: 'item-008',
    name: 'House & Car Keys',
    category: 'Keys',
    brand: 'Personal',
    status: 'PROTECTED',
    labelId: 'RT-K05F',
    lastScan: null,
    Icon: Key,
    iconBg: 'bg-yellow-50',
    iconColor: 'text-yellow-600',
  },
];

const statusConfig: Record<string, { label: string; className: string; Icon: React.ElementType }> = {
  PROTECTED: { label: 'Protected', className: 'status-protected', Icon: ShieldCheck },
  LOST: { label: 'LOST', className: 'status-lost', Icon: AlertTriangle },
};

export default function ItemGrid() {
  return (
    <section className="mt-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-700 text-lg text-foreground">My Items</h2>
        <button className="text-accent text-sm font-600 hover:underline">View all</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map((item) => {
          const status = statusConfig[item.status] || statusConfig.PROTECTED;
          const StatusIcon = status.Icon;
          const isLost = item.status === 'LOST';

          return (
            <div
              key={item.id}
              className={`bg-white rounded-2xl border p-4 flex flex-col gap-3 card-hover cursor-pointer relative overflow-hidden ${
                isLost ? 'border-red-200 bg-red-50/30' : 'border-border'
              }`}
            >
              {isLost && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-red-500 rounded-t-2xl lost-pulse" />
              )}

              <div className="flex items-start justify-between">
                <div className={`w-10 h-10 rounded-xl ${item.iconBg} flex items-center justify-center`}>
                  <item.Icon size={18} className={item.iconColor} />
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-700 uppercase tracking-wide px-2 py-1 rounded-full ${status.className}`}>
                    <StatusIcon size={9} />
                    {status.label}
                  </span>
                  <button className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Item options">
                    <MoreHorizontal size={15} />
                  </button>
                </div>
              </div>

              <div>
                <p className="font-700 text-sm text-foreground leading-tight">{item.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{item.brand} · {item.category}</p>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-border">
                <div>
                  <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground">Label</p>
                  <p className="text-xs font-600 text-foreground tabular-nums">{item.labelId}</p>
                </div>
                {item.lastScan && (
                  <div className="text-right">
                    <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground">Last Scan</p>
                    <p className="text-xs text-muted-foreground">{item.lastScan}</p>
                  </div>
                )}
              </div>

              {isLost && (
                <Link
                  href="/lost-mode"
                  className="flex items-center justify-center gap-1.5 bg-red-500 text-white text-xs font-700 px-3 py-2 rounded-xl hover:bg-red-600 transition-colors"
                >
                  <AlertTriangle size={11} />
                  View Recovery
                  <ChevronRight size={11} />
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}